/**
 * Screenshots attached to a report.
 *
 * A picture of the broken thing settles in one glance what a paragraph
 * argues about, so this is worth the weight it adds. Three things make it
 * worth using rather than ignoring:
 *
 *   - Paste works. On a Mac the whole gesture is Cmd+Shift+4, Cmd+V. An
 *     attach flow that forces a round trip through Finder is one most
 *     people abandon.
 *   - Only images. A PDF or a zip in a feedback tracker is a support
 *     ticket, which is a different product.
 *   - A hard cap on count and size, enforced here rather than discovered
 *     at upload time.
 *
 * The privacy cost is real and is not hidden: a screenshot of the planner
 * carries whatever was on screen, which is usually a client's data. The
 * form says so. That is a decision for the person attaching it, which is
 * why it is a decision and not a default.
 *
 * Pure. Takes files and lists, returns new lists.
 */

export const MAX_SHOTS = 3;
export const MAX_SHOT_BYTES = 5 * 1024 * 1024;

/**
 * What a browser reports for a pasted or dropped screenshot.
 *
 * GIF is absent on purpose: the server validates the decoded bytes and
 * accepts PNG, JPEG and WebP only. Accepting one here that the server
 * refuses would move the rejection to submit time, which is the thing
 * this module exists to prevent.
 */
export const ACCEPTED_TYPES = ['image/png', 'image/jpeg', 'image/webp'];

/** For the file picker, which matches on extension rather than type. */
export const ACCEPTED_EXTENSIONS = '.png,.jpg,.jpeg,.webp';

// Unique within the session, which is all an attachment list needs. Two
// screenshots pasted in the same millisecond would otherwise collide.
let counter = 0;

export function formatBytes(bytes) {
  const n = Number(bytes) || 0;
  if (n >= 1024 * 1024) return `${(n / (1024 * 1024)).toFixed(1)} MB`;
  if (n >= 1024) return `${Math.round(n / 1024)} KB`;
  return `${n} B`;
}

const isImage = (file) => !!file && ACCEPTED_TYPES.indexOf(file.type) !== -1;

/**
 * Why this file cannot be attached, or null.
 *
 * `count` is how many are already attached, so the message can say what is
 * actually wrong rather than a generic refusal.
 */
export function shotError(file, count) {
  if (!file) return 'That did not come through as a file.';
  if (!isImage(file)) {
    return 'Images only — PNG, JPG or WebP. For anything else, describe it above.';
  }
  if (file.size > MAX_SHOT_BYTES) {
    return `That is ${formatBytes(file.size)}. Keep each image under ${formatBytes(
      MAX_SHOT_BYTES,
    )}.`;
  }
  if (count >= MAX_SHOTS) {
    return `${MAX_SHOTS} images is the limit. Remove one to add another.`;
  }
  return null;
}

/**
 * A pasted screenshot usually arrives as `image.png`, or with no name at
 * all. Numbering them keeps the list readable.
 */
function nameFor(file) {
  const given = (file.name || '').trim();
  if (given && given !== 'image.png') return given;
  const extension = (file.type || 'image/png').split('/')[1] || 'png';
  return `screenshot-${counter + 1}.${extension}`;
}

export function shotFromFile(file) {
  counter += 1;
  return {
    id: `shot-${counter}`,
    file,
    name: nameFor(file),
    size: file.size,
    type: file.type,
  };
}

/**
 * Add what was picked, pasted or dropped.
 *
 * Accepts the good ones and reports the rest, rather than rejecting the
 * whole batch because one file was a PDF. Returns a new list either way.
 */
export function addShots(existing, files) {
  const current = Array.isArray(existing) ? existing : [];
  const incoming = Array.from(files || []);

  return incoming.reduce(
    (acc, file) => {
      const problem = shotError(file, acc.shots.length);
      if (problem) return { ...acc, errors: [...acc.errors, problem] };
      return { ...acc, shots: [...acc.shots, shotFromFile(file)] };
    },
    { shots: current, errors: [] },
  );
}

export function removeShot(shots, id) {
  return (Array.isArray(shots) ? shots : []).filter((shot) => shot.id !== id);
}

/**
 * The image files on a paste.
 *
 * `clipboardData.items` also carries a `text/html` entry for a screenshot
 * copied out of a browser, so filtering on kind and type matters.
 */
export function filesFromPaste(clipboardData) {
  const items = (clipboardData && clipboardData.items) || [];
  return Array.from(items)
    .filter((entry) => entry.kind === 'file' && String(entry.type || '').indexOf('image/') === 0)
    .map((entry) => entry.getAsFile())
    .filter(Boolean);
}

/** The image files on a drop, ignoring dragged text and links. */
export function filesFromDrop(dataTransfer) {
  const files = (dataTransfer && dataTransfer.files) || [];
  return Array.from(files).filter((file) => String(file.type || '').indexOf('image/') === 0);
}

/** How many more will fit, for the prompt. */
export function roomLeft(shots) {
  return Math.max(0, MAX_SHOTS - (Array.isArray(shots) ? shots.length : 0));
}
